/**
 * Shades of India — storefront interactions for the soi-* sections.
 * Ports the behaviour of the approved prototypes (script.js / desktop-script.js) to data-attribute
 * driven components that work per section, in the theme editor, and inside EComposer embeds.
 * Commerce modules (search autocomplete, AJAX filters, cart drawer, favourites, size charts, currency,
 * back-in-stock) live in the Shades of India Control app and hook into the same markup through events:
 *   soi:cart:added, soi:cart:updated, soi:variant:change, soi:search:open, soi:filter:navigate
 */
(() => {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const emit = (name, detail, target = document) => {
    const ev = new CustomEvent(name, { detail, bubbles: true, cancelable: true });
    return target.dispatchEvent(ev);
  };
  const isVisible = (el) => !!(el && el.offsetParent !== null);

  /* ---------- Swipe helper ---------- */
  function onSwipe(el, cb, threshold = 35) {
    let x0 = null;
    let y0 = null;
    el.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; y0 = e.touches[0].clientY; }, { passive: true });
    el.addEventListener('touchend', (e) => {
      if (x0 === null) return;
      const dx = e.changedTouches[0].clientX - x0;
      const dy = e.changedTouches[0].clientY - y0;
      if (Math.abs(dx) > threshold && Math.abs(dx) > Math.abs(dy)) cb(dx < 0 ? 1 : -1);
      x0 = null;
    }, { passive: true });
  }

  /* ---------- Autoplay helper: runs only while visible and the tab is active ----------
     Carousels rotate on their own and loop. They pause while the pointer is over them, while a
     control inside has focus, while a finger is on the slide, and whenever the tab is hidden. */
  function autoplay(el, ms, tick) {
    if (!ms || reduceMotion) return { reset() {} };
    let timer = null;
    let inView = false;
    let held = false;
    const stop = () => { clearInterval(timer); timer = null; };
    const start = () => { stop(); if (inView && !held && !document.hidden) timer = setInterval(tick, ms); };
    new IntersectionObserver((entries) => { inView = entries[0].isIntersecting; start(); }, { threshold: 0.25 }).observe(el);
    document.addEventListener('visibilitychange', start);
    el.addEventListener('mouseenter', () => { held = true; stop(); });
    el.addEventListener('mouseleave', () => { held = false; start(); });
    el.addEventListener('focusin', () => { held = true; stop(); });
    el.addEventListener('focusout', () => { held = false; start(); });
    el.addEventListener('touchstart', () => { held = true; stop(); }, { passive: true });
    const release = () => { held = false; clearTimeout(el.soiHold); el.soiHold = setTimeout(start, 4000); };
    el.addEventListener('touchend', release, { passive: true });
    el.addEventListener('touchcancel', release, { passive: true });
    return { reset: start };
  }

  // How fast each kind of carousel rotates when the section has not set data-autoplay itself.
  // data-autoplay="0" switches rotation off for that one carousel.
  function autoplayMs(root, fallback) {
    const raw = root.dataset.autoplay;
    if (raw !== undefined && raw !== '') return parseInt(raw, 10) || 0;
    if (root.hasAttribute('data-soi-gallery') || root.closest('[data-soi-product]')) return 0;
    return fallback;
  }

  /* ---------- Sliding carousel (hero, NCR stores, desktop stores, PDP gallery) ---------- */
  function initCarousel(root) {
    if (root.soiCarousel) return root.soiCarousel;
    const track = root.querySelector('[data-soi-track]');
    if (!track) return null;
    const slides = Array.from(track.children);
    const dots = $$('[data-soi-dot]', root).filter((d) => d.closest('[data-soi-carousel]') === root);
    const counter = root.querySelector('[data-soi-counter]');
    const cta = root.querySelector('[data-soi-slide-cta]');
    let index = 0;
    const go = (i) => {
      if (!slides.length) return;
      index = (i + slides.length) % slides.length;
      track.style.transform = `translateX(-${index * 100}%)`;
      slides.forEach((s, n) => s.classList.toggle('active', n === index));
      dots.forEach((d, n) => d.classList.toggle('active', n === index));
      if (counter) counter.textContent = String(index + 1);
      if (cta) {
        const s = slides[index];
        if (s.dataset.link) cta.setAttribute('href', s.dataset.link);
        if (s.dataset.label) cta.textContent = s.dataset.label;
      }
      $$('video[data-soi-autoplay]', track).forEach((v, n) => {
        const inSlide = slides[index].contains(v);
        if (inSlide) playVideo(v); else v.pause();
      });
    };
    const ap = autoplay(root, autoplayMs(root, 5500), () => go(index + 1));
    const step = (d) => { go(index + d); ap.reset(); };
    $$('[data-soi-prev]', root).forEach((b) => b.closest('[data-soi-carousel]') === root && b.addEventListener('click', () => step(-1)));
    $$('[data-soi-next]', root).forEach((b) => b.closest('[data-soi-carousel]') === root && b.addEventListener('click', () => step(1)));
    dots.forEach((d) => {
      const handler = () => { go(parseInt(d.dataset.soiDot, 10)); ap.reset(); };
      d.addEventListener('click', handler);
      d.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handler(); } });
    });
    if (root.hasAttribute('data-soi-swipe')) onSwipe(root, step, 40);
    root.soiCarousel = { go, get index() { return index; }, slides };
    return root.soiCarousel;
  }

  /* ---------- Crossfade (featured slideshow, desktop festive banner) ---------- */
  function initFader(root) {
    const slides = $$('[data-soi-slide]', root);
    const dots = $$('[data-soi-dot]', root);
    if (slides.length < 2) return;
    let index = 0;
    const go = (i) => {
      index = (i + slides.length) % slides.length;
      slides.forEach((s, n) => {
        s.classList.toggle('is-active', n === index);
        $$('.featured-slide-img', s).forEach((img) => img.classList.toggle('active', n === index));
      });
      dots.forEach((d, n) => d.classList.toggle('active', n === index));
    };
    const ap = autoplay(root, autoplayMs(root, 6000), () => go(index + 1));
    root.querySelector('[data-soi-prev]')?.addEventListener('click', () => { go(index - 1); ap.reset(); });
    root.querySelector('[data-soi-next]')?.addEventListener('click', () => { go(index + 1); ap.reset(); });
    dots.forEach((d) => d.addEventListener('click', () => { go(parseInt(d.dataset.soiDot, 10)); ap.reset(); }));
    onSwipe(root, (d) => { go(index + d); ap.reset(); });
  }

  /* ---------- Maroon announcement banner (slide left/right) ---------- */
  function initMaroon(root) {
    const slides = $$('[data-soi-slide]', root);
    if (!slides.length) return;
    let index = 0;
    const go = (i) => {
      index = (i + slides.length) % slides.length;
      slides.forEach((s, n) => {
        s.classList.remove('active', 'slide-prev');
        if (n === index) s.classList.add('active');
        else if (n < index) s.classList.add('slide-prev');
      });
    };
    const ap = autoplay(root, slides.length > 1 ? parseInt(root.dataset.autoplay || '0', 10) : 0, () => go(index + 1));
    root.querySelector('[data-soi-prev]')?.addEventListener('click', () => { go(index - 1); ap.reset(); });
    root.querySelector('[data-soi-next]')?.addEventListener('click', () => { go(index + 1); ap.reset(); });
    onSwipe(root, (d) => { go(index + d); ap.reset(); }, 30);
  }

  /* ---------- 3D stacked services carousel ---------- */
  function initStack(root) {
    const cards = $$('[data-soi-slide]', root);
    const section = root.closest('section') || root.parentElement;
    const dots = $$('[data-soi-dot]', section);
    if (!cards.length) return;
    let active = 0;
    const update = () => {
      const total = cards.length;
      cards.forEach((card, i) => {
        card.classList.remove('active-front', 'next-behind', 'prev-behind', 'hidden-stack');
        if (i === active) card.classList.add('active-front');
        else if (i === (active + 1) % total) card.classList.add('next-behind');
        else if (i === (active - 1 + total) % total) card.classList.add('prev-behind');
        else card.classList.add('hidden-stack');
        const v = card.querySelector('video');
        if (v) { if (i === active) playVideo(v); else v.pause(); }
      });
      dots.forEach((d, i) => d.classList.toggle('active', i === active));
    };
    onSwipe(root, (d) => { active = (active + d + cards.length) % cards.length; update(); });
    cards.forEach((card, i) => card.addEventListener('click', (e) => {
      if (i !== active) { e.preventDefault(); active = i; update(); }
    }));
    dots.forEach((d, i) => d.addEventListener('click', () => { active = i; update(); }));
    update();
  }

  /* ---------- Horizontal scroller with pagination dots (Shades for you cards) ---------- */
  function initScroller(root) {
    const section = root.closest('section') || root.parentElement;
    const dots = $$('[data-soi-dot]', section);
    const cards = Array.from(root.children);
    if (!cards.length) return;
    const gap = () => parseFloat(getComputedStyle(root).columnGap || getComputedStyle(root).gap) || 18;
    root.addEventListener('scroll', () => {
      const w = cards[0].offsetWidth + gap();
      const i = Math.min(dots.length - 1, Math.round(root.scrollLeft / w));
      dots.forEach((d, n) => d.classList.toggle('active', n === i));
    }, { passive: true });
    dots.forEach((d) => d.addEventListener('click', () => {
      const w = cards[0].offsetWidth + gap();
      root.scrollTo({ left: parseInt(d.dataset.soiDot, 10) * w, behavior: 'smooth' });
    }));
  }

  /* ---------- Videos: play only when on screen and rendered ---------- */
  function playVideo(v) {
    if (!isVisible(v)) return;
    if (v.preload === 'none') v.preload = 'auto';
    const p = v.play();
    if (p && p.catch) p.catch(() => {});
  }
  const videoObserver = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const v = entry.target;
      if (entry.isIntersecting) {
        const slide = v.closest('.carousel-slide, .stacked-card-item');
        const inactive = slide && !slide.classList.contains('active') && !slide.classList.contains('active-front');
        if (!inactive && !v.dataset.userPaused && !reduceMotion) playVideo(v);
      } else {
        v.pause();
      }
    });
  }, { threshold: 0.3 }) : null;

  function initVideoToggle(frame) {
    const video = frame.querySelector('video');
    const btn = frame.querySelector('[data-soi-video-button]');
    if (!video || !btn) return;
    const pause = btn.querySelector('.icon-pause');
    const play = btn.querySelector('.icon-play');
    const sync = () => {
      if (pause) pause.style.display = video.paused ? 'none' : 'block';
      if (play) play.style.display = video.paused ? 'block' : 'none';
    };
    const toggle = (e) => {
      e.stopPropagation();
      if (video.paused) { delete video.dataset.userPaused; playVideo(video); } else { video.dataset.userPaused = '1'; video.pause(); }
    };
    btn.addEventListener('click', toggle);
    video.addEventListener('click', toggle);
    video.addEventListener('play', sync);
    video.addEventListener('pause', sync);
    sync();
  }

  /* ---------- Sticky headers ---------- */
  function initSticky() {
    const headers = $$('[data-soi-sticky]');
    if (!headers.length) return;
    const onScroll = () => {
      headers.forEach((h) => {
        const cls = h.closest('.soi-d') ? 'sticky' : 'sticky-scrolled';
        h.classList.toggle(cls, window.scrollY > parseInt(h.dataset.soiSticky, 10));
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ---------- Announcement height (positions the mobile header over the home hero) ---------- */
  function initAnnouncementOffset() {
    const set = () => {
      const bar = $$('[data-soi-announcement]').find(isVisible);
      document.documentElement.style.setProperty('--soi-ann-h', `${bar ? bar.offsetHeight : 0}px`);
    };
    set();
    window.addEventListener('resize', set, { passive: true });
    if ('ResizeObserver' in window) $$('[data-soi-announcement]').forEach((b) => new ResizeObserver(set).observe(b));
  }

  /* ---------- Desktop mega menu ---------- */
  function initMega() {
    const items = $$('.soi-d .nav-item.has-mega');
    items.forEach((item) => {
      let t;
      const open = () => { clearTimeout(t); items.forEach((i) => i !== item && i.classList.remove('open')); item.classList.add('open'); };
      const close = () => { t = setTimeout(() => item.classList.remove('open'), 150); };
      item.addEventListener('mouseenter', open);
      item.addEventListener('mouseleave', close);
      item.addEventListener('focusin', open);
      item.addEventListener('focusout', (e) => { if (!item.contains(e.relatedTarget)) close(); });
    });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') items.forEach((i) => i.classList.remove('open')); });
  }

  /* ---------- EComposer mega menu sections (Section Rendering API) ---------- */
  const ecomCache = {};
  async function loadEcomSection(el) {
    const handle = el.dataset.soiEcomSection;
    if (!handle || el.dataset.soiEcomLoaded) return;
    el.dataset.soiEcomLoaded = 'loading';
    try {
      ecomCache[handle] = ecomCache[handle] || fetch(`${window.Shopify?.routes?.root || '/'}?section_id=${encodeURIComponent(handle)}`)
        .then((r) => (r.ok ? r.text() : ''));
      const html = await ecomCache[handle];
      if (!html || !html.trim()) { el.dataset.soiEcomLoaded = 'empty'; return; }
      const wrap = document.createElement('div');
      wrap.className = `soi-ecom-mega soi-ecom-mega--${el.dataset.soiEcomMode || 'desktop'}`;
      wrap.innerHTML = html;
      // Execute EComposer's inline scripts (sliders, tabs) after injection
      wrap.querySelectorAll('script').forEach((old) => {
        const sc = document.createElement('script');
        [...old.attributes].forEach((a) => sc.setAttribute(a.name, a.value));
        sc.textContent = old.textContent;
        old.replaceWith(sc);
      });
      el.classList.add('has-ecom');
      el.replaceChildren(wrap);
      el.dataset.soiEcomLoaded = 'done';
      emit('soi:content:loaded', { root: el });
    } catch (err) {
      el.dataset.soiEcomLoaded = 'error';
    }
  }
  function initEcomMega() {
    $$('.soi-d .nav-item [data-soi-ecom-section]').forEach((panel) => {
      const item = panel.closest('.nav-item');
      const warm = () => loadEcomSection(panel);
      item.addEventListener('mouseenter', warm, { once: true });
      item.addEventListener('focusin', warm, { once: true });
    });
    const drawerPanels = $$('[data-soi-drawer] [data-soi-ecom-section]');
    if (drawerPanels.length) {
      const loadVisible = () => drawerPanels.forEach((p) => { if (p.style.display !== 'none') loadEcomSection(p); });
      $$('[data-soi-drawer-open]').forEach((t) => t.addEventListener('click', loadVisible));
      $$('[data-soi-drawer] [data-soi-tab]').forEach((t) => t.addEventListener('click', () => setTimeout(loadVisible, 0)));
    }
    // Warm desktop menus when idle so the first hover is instant
    if ('requestIdleCallback' in window && window.matchMedia('(min-width: 1100px)').matches) {
      requestIdleCallback(() => $$('.soi-d .nav-item [data-soi-ecom-section]').forEach(loadEcomSection));
    }
  }

  /* ---------- Mobile drawer ---------- */
  function initDrawer() {
    const drawer = document.querySelector('[data-soi-drawer]');
    if (!drawer) return;
    const overlay = drawer.parentElement.querySelector('.nav-drawer-overlay');
    const toggles = $$('[data-soi-drawer-open]');
    const setOpen = (open) => {
      drawer.classList.toggle('active', open);
      overlay?.classList.toggle('active', open);
      drawer.setAttribute('aria-hidden', String(!open));
      toggles.forEach((t) => t.setAttribute('aria-expanded', String(open)));
      document.body.classList.toggle('soi-lock', open);
    };
    toggles.forEach((t) => t.addEventListener('click', () => setOpen(true)));
    $$('[data-soi-drawer-close]').forEach((c) => c.addEventListener('click', () => setOpen(false)));
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') setOpen(false); });
    document.addEventListener('soi:drawer:close', () => setOpen(false));
  }

  /* ---------- Tabs (drawer categories, PDP details) ---------- */
  function initTabs(root) {
    const tabs = $$('[data-soi-tab]', root);
    tabs.forEach((tab) => tab.addEventListener('click', () => {
      tabs.forEach((t) => { t.classList.toggle('active', t === tab); t.setAttribute('aria-selected', String(t === tab)); });
      $$('[data-soi-tab-panel]', root).forEach((p) => {
        const on = p.dataset.soiTabPanel === tab.dataset.soiTab;
        p.style.display = on ? (p.classList.contains('drawer-cards-list') ? 'flex' : 'block') : 'none';
        p.classList.toggle('active', on);
      });
    }));
  }

  /* ---------- Search overlay (autocomplete/NLP added by the app search module) ---------- */
  function initSearch() {
    const overlay = document.querySelector('[data-soi-search-overlay]');
    if (!overlay) return;
    const input = overlay.querySelector('[data-soi-search-input]');
    const setOpen = (open) => {
      overlay.classList.toggle('active', open);
      overlay.setAttribute('aria-hidden', String(!open));
      document.body.classList.toggle('soi-lock', open);
      if (open) { setTimeout(() => input?.focus(), 150); emit('soi:search:open', { overlay }); }
    };
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-soi-search-open]');
      if (!trigger) return;
      e.preventDefault();
      document.dispatchEvent(new CustomEvent('soi:drawer:close'));
      setOpen(true);
    });
    overlay.querySelector('[data-soi-search-close]')?.addEventListener('click', () => { setOpen(false); if (input) input.value = ''; });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && overlay.classList.contains('active')) setOpen(false); });
    document.addEventListener('soi:search:close', () => setOpen(false));
  }

  /* ---------- Footer accordions (prototype behaviour: one open at a time) ---------- */
  function initAccordions(root) {
    $$('[data-soi-accordion]', root).forEach((item) => {
      const header = item.querySelector('.accordion-header');
      const content = item.querySelector('.accordion-content');
      header?.addEventListener('click', () => {
        const isActive = item.classList.contains('active');
        $$('[data-soi-accordion]').forEach((other) => {
          if (other !== item) {
            other.classList.remove('active');
            const c = other.querySelector('.accordion-content');
            if (c) c.style.maxHeight = null;
            other.querySelector('.accordion-header')?.setAttribute('aria-expanded', 'false');
          }
        });
        item.classList.toggle('active', !isActive);
        header.setAttribute('aria-expanded', String(!isActive));
        content.style.maxHeight = isActive ? null : `${content.scrollHeight}px`;
      });
    });
    // PDP desktop accordions
    $$('[data-soi-acc]', root).forEach((acc) => {
      const head = acc.querySelector('.soi-acc__head');
      if (!head || head.hasAttribute('data-soi-size-chart')) return;
      head.addEventListener('click', () => {
        const open = !acc.classList.contains('is-open');
        acc.classList.toggle('is-open', open);
        head.setAttribute('aria-expanded', String(open));
      });
    });
  }

  /* ---------- Store locator ---------- */
  function initStores(root) {
    const select = root.querySelector('[data-soi-store-select]');
    if (!select) return;
    select.addEventListener('change', () => {
      $$('[data-soi-store-region]', root).forEach((el) => {
        const on = el.dataset.soiStoreRegion === select.value;
        el.hidden = !on;
        if (on && el.soiCarousel) el.soiCarousel.go(0);
      });
    });
  }

  /* ---------- Back buttons ---------- */
  function initBack(root) {
    $$('[data-soi-back]', root).forEach((a) => a.addEventListener('click', (e) => {
      if (document.referrer && new URL(document.referrer).origin === location.origin && history.length > 1) {
        e.preventDefault();
        history.back();
      }
    }));
  }

  /* ---------- Cart helpers ---------- */
  const Cart = {
    async add(items) {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || 'Could not add to bag');
      emit('soi:cart:added', { items: data.items || [data] });
      Cart.refresh();
      return data;
    },
    async refresh() {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
      const cart = await res.json();
      emit('soi:cart:updated', { cart });
      return cart;
    },
    hasDrawer: () => !!(window.SOI && window.SOI.cart && window.SOI.cart.enabled),
  };
  document.addEventListener('soi:cart:updated', (e) => {
    const count = e.detail?.cart?.item_count ?? 0;
    $$('[data-soi-cart-count]').forEach((b) => { b.textContent = String(count); b.hidden = count === 0; });
  });

  /* ---------- Product page ---------- */
  function initProduct(root) {
    const jsonEl = root.querySelector('[data-soi-product-json]');
    if (!jsonEl) return;
    const product = JSON.parse(jsonEl.textContent);
    const form = root.querySelector('[data-soi-product-form]');
    const idInput = root.querySelector('[data-soi-variant-id]');
    const optionGroups = $$('[data-soi-option]', root);
    const priceEl = root.querySelector('[data-soi-price-current]');
    const compareEl = root.querySelector('[data-soi-price-compare]');
    const addBtn = root.querySelector('[data-soi-add]');
    const addLabel = root.querySelector('[data-soi-add-label]');
    const errorEl = root.querySelector('[data-soi-form-error]');
    const bis = root.querySelector('[data-soi-bis]');
    const gallery = root.querySelector('[data-soi-gallery]');
    const addText = addLabel ? addLabel.textContent : 'ADD TO BAG';
    const moneyFormat = (window.theme && window.theme.moneyFormat) || '₹{{amount_no_decimals}}';

    const formatMoney = (cents) => {
      const value = (cents / 100);
      const withDelims = (n, d) => n.toFixed(d).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (m, key) => {
        if (key === 'amount_no_decimals') return withDelims(value, 0);
        if (key === 'amount_with_comma_separator') return withDelims(value, 2).replace('.', ',');
        return withDelims(value, 2);
      }).replace(/\.00(?!\d)/, '').replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
    };
    const selected = () => optionGroups.map((g) => g.querySelector('.pdp-size-box.active')?.dataset.value);
    const findVariant = (opts) => product.variants.find((v) => v.options.every((o, i) => o === opts[i]));

    const markAvailability = () => {
      const current = selected();
      optionGroups.forEach((group, gi) => {
        $$('.pdp-size-box', group).forEach((box) => {
          const trial = current.slice();
          trial[gi] = box.dataset.value;
          const v = findVariant(trial);
          box.classList.toggle('is-unavailable', !v || !v.available);
        });
      });
    };

    const applyVariant = (variant) => {
      if (!variant) {
        if (addBtn) addBtn.disabled = true;
        if (addLabel) addLabel.textContent = 'Unavailable';
        return;
      }
      idInput.value = variant.id;
      if (priceEl) priceEl.textContent = formatMoney(variant.price);
      if (compareEl) {
        compareEl.hidden = !(variant.compare_at_price > variant.price);
        if (!compareEl.hidden) compareEl.textContent = formatMoney(variant.compare_at_price);
      }
      if (addBtn) addBtn.disabled = !variant.available;
      if (addLabel) addLabel.textContent = variant.available ? addText : 'Sold out';
      if (bis) { bis.dataset.variantId = variant.id; bis.hidden = variant.available; }
      if (gallery && variant.featured_media) {
        const slides = $$('.pdp-gallery-slide', gallery);
        const idx = slides.findIndex((s) => s.dataset.mediaId === String(variant.featured_media.id));
        if (idx > -1 && gallery.soiCarousel) gallery.soiCarousel.go(idx);
      }
      const url = new URL(location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState(history.state, '', url);
      markAvailability();
      emit('soi:variant:change', { product, variant }, root);
    };

    optionGroups.forEach((group) => {
      $$('.pdp-size-box', group).forEach((box) => box.addEventListener('click', () => {
        $$('.pdp-size-box', group).forEach((b) => { b.classList.toggle('active', b === box); b.setAttribute('aria-checked', String(b === box)); });
        applyVariant(findVariant(selected()));
      }));
    });
    markAvailability();

    // Quantity stepper
    const qty = root.querySelector('[data-soi-qty] input');
    $$('[data-soi-qty-step]', root).forEach((b) => b.addEventListener('click', () => {
      const next = Math.max(1, (parseInt(qty.value, 10) || 1) + parseInt(b.dataset.soiQtyStep, 10));
      qty.value = String(next);
    }));

    // Sticky add-to-bag bar: shown once the main button is off-screen after scrolling; drives the main form
    const sticky = root.querySelector('[data-soi-sticky-atc]');
    const mainActions = root.querySelector('.pdp-actions-wrapper');
    if (sticky && mainActions && addBtn && qty) {
      const sQty = sticky.querySelector('[data-soi-sticky-qty]');
      const sBtn = sticky.querySelector('[data-soi-sticky-add]');
      const sLabel = sticky.querySelector('[data-soi-sticky-label]');
      const syncQty = () => { sQty.textContent = String(Math.max(1, parseInt(qty.value, 10) || 1)); };
      const syncBtn = () => { sBtn.disabled = addBtn.disabled; if (addLabel) sLabel.textContent = addLabel.textContent; sBtn.classList.toggle('is-loading', addBtn.classList.contains('is-loading')); };
      $$('[data-soi-sticky-step]', sticky).forEach((b) => b.addEventListener('click', () => {
        qty.value = String(Math.max(1, (parseInt(qty.value, 10) || 1) + parseInt(b.dataset.soiStickyStep, 10)));
        syncQty();
      }));
      $$('[data-soi-qty-step]', root).forEach((b) => b.addEventListener('click', syncQty));
      qty.addEventListener('input', syncQty);
      sBtn.addEventListener('click', () => addBtn.click());
      new MutationObserver(syncBtn).observe(addBtn, { attributes: true, childList: true, subtree: true, characterData: true });
      syncQty(); syncBtn();
      let mainVisible = true;
      const update = () => {
        const nearEnd = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 120; // let the footer show
        const show = !mainVisible && window.scrollY > 200 && !nearEnd;
        sticky.classList.toggle('is-visible', show);
        sticky.setAttribute('aria-hidden', String(!show));
        $$('button', sticky).forEach((b) => { b.tabIndex = show ? 0 : -1; });
      };
      if ('IntersectionObserver' in window) {
        new IntersectionObserver((entries) => { mainVisible = entries[0].isIntersecting; update(); }).observe(mainActions);
        window.addEventListener('scroll', update, { passive: true });
      }
    }

    // Add to bag
    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (errorEl) errorEl.hidden = true;
      addBtn?.classList.add('is-loading');
      try {
        const properties = {};
        new FormData(form).forEach((val, key) => {
          const m = key.match(/^properties\[(.+)\]$/);
          if (m) properties[m[1]] = val;
        });
        await Cart.add([{ id: Number(idInput.value), quantity: Math.max(1, parseInt(qty?.value || '1', 10)), properties }]);
        if (!Cart.hasDrawer()) window.location.href = window.theme?.routes?.cart_url || '/cart';
        else if (addLabel) {
          addLabel.textContent = 'ADDED ✓';
          setTimeout(() => { addLabel.textContent = addText; }, 1600);
        }
      } catch (err) {
        if (errorEl) { errorEl.textContent = err.message; errorEl.hidden = false; }
      } finally {
        addBtn?.classList.remove('is-loading');
      }
    });

    // Cross-sell add ("Style it with" / "Pair it with")
    root.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-soi-cross-add]');
      if (!btn) return;
      const card = btn.closest('[data-soi-cross-sell]');
      const sel = card.querySelector('[data-soi-cross-variant]');
      if (!sel.value) { sel.classList.add('is-invalid'); sel.focus(); return; }
      sel.classList.remove('is-invalid');
      btn.disabled = true;
      try {
        await Cart.add([{ id: Number(sel.value), quantity: 1 }]);
        btn.classList.add('is-added');
        if (!Cart.hasDrawer()) window.location.href = window.theme?.routes?.cart_url || '/cart';
      } catch (err) {
        if (errorEl) { errorEl.textContent = err.message; errorEl.hidden = false; }
      } finally {
        btn.disabled = false;
        setTimeout(() => btn.classList.remove('is-added'), 1600);
      }
    });

    // Size guide modal (content provided by the app size-chart module when configured)
    const modal = root.querySelector('[data-soi-size-modal]');
    const setModal = (open) => {
      if (!modal) return;
      modal.classList.toggle('active', open);
      modal.setAttribute('aria-hidden', String(!open));
      document.body.classList.toggle('soi-lock', open);
      if (open) emit('soi:size-chart:open', { product, body: modal.querySelector('[data-soi-size-body]') }, root);
    };
    $$('[data-soi-size-chart]', root).forEach((t) => t.addEventListener('click', (e) => { e.preventDefault(); setModal(true); }));
    $$('[data-soi-size-close]', root).forEach((c) => c.addEventListener('click', () => setModal(false)));
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') setModal(false); });
  }

  /* ---------- Product image lightbox (carousel gallery with thumbnails + zoom) ---------- */
  function openLightbox(gallery, startIndex) {
    const slides = $$('[data-soi-zoom]', gallery);
    if (!slides.length) return;
    const images = slides.map((s) => ({ src: s.dataset.soiZoom, thumb: s.dataset.soiThumb, alt: s.dataset.alt || '', video: s.dataset.soiVideo || '', embed: s.dataset.soiEmbed || '' }));
    let index = Math.max(0, slides.findIndex((s) => s === startIndex) );
    if (typeof startIndex === 'number') index = startIndex;
    const box = document.createElement('div');
    box.className = 'soi-lightbox';
    box.setAttribute('role', 'dialog');
    box.setAttribute('aria-modal', 'true');
    box.setAttribute('aria-label', 'Product images');
    box.innerHTML = `
      <div class="soi-lightbox__top">
        <span class="soi-lightbox__count" aria-live="polite"></span>
        <button type="button" class="soi-lightbox__close" aria-label="Close">&times;</button>
      </div>
      <div class="soi-lightbox__stage">
        <button type="button" class="soi-lightbox__arrow soi-lightbox__arrow--prev" aria-label="Previous image">&#10094;</button>
        <div class="soi-lightbox__viewport"><div class="soi-lightbox__track">${images.map((im) => (im.video
          ? `<figure class="soi-lightbox__slide is-video"><video src="${im.video}" poster="${im.src}" controls playsinline loop preload="metadata"></video></figure>`
          : im.embed
            ? `<figure class="soi-lightbox__slide is-video"><iframe data-src="${im.embed}" title="${im.alt.replace(/"/g, '&quot;')}" allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowfullscreen></iframe></figure>`
            : `<figure class="soi-lightbox__slide"><img src="${im.src}" alt="${im.alt.replace(/"/g, '&quot;')}" draggable="false" loading="lazy"></figure>`)).join('')}</div></div>
        <button type="button" class="soi-lightbox__arrow soi-lightbox__arrow--next" aria-label="Next image">&#10095;</button>
      </div>
      <div class="soi-lightbox__thumbs">${images.map((im, i) => `<button type="button" class="soi-lightbox__thumb${im.video || im.embed ? ' is-video' : ''}" data-i="${i}" aria-label="${im.video || im.embed ? 'Video' : 'Image'} ${i + 1}"><img src="${im.thumb}" alt="" loading="lazy"></button>`).join('')}</div>
      <p class="soi-lightbox__hint">Tap image to zoom</p>`;
    document.body.appendChild(box);
    document.body.classList.add('soi-lock');
    const track = box.querySelector('.soi-lightbox__track');
    const count = box.querySelector('.soi-lightbox__count');
    const thumbs = $$('.soi-lightbox__thumb', box);
    const figs = $$('.soi-lightbox__slide', box);
    let zoom = null;
    const resetZoom = () => {
      if (!zoom) return;
      zoom.img.style.transform = '';
      zoom.fig.classList.remove('is-zoomed');
      zoom = null;
    };
    const go = (i) => {
      resetZoom();
      index = (i + images.length) % images.length;
      track.style.transform = `translateX(-${index * 100}%)`;
      count.textContent = `${index + 1} / ${images.length}`;
      thumbs.forEach((t, n) => t.classList.toggle('is-active', n === index));
      thumbs[index]?.scrollIntoView({ block: 'nearest', inline: 'center' });
      // videos: only the visible one plays
      figs.forEach((f, n) => {
        const v = f.querySelector('video');
        if (v) { if (n === index) { v.play().catch(() => {}); } else { v.pause(); } }
        const fr = f.querySelector('iframe');
        if (fr) {
          if (n === index) { if (!fr.src) fr.src = fr.dataset.src; }
          else if (fr.src) { fr.removeAttribute('src'); }
        }
      });
    };
    const close = () => {
      figs.forEach((f) => { const v = f.querySelector('video'); if (v) v.pause(); const fr = f.querySelector('iframe'); if (fr) fr.removeAttribute('src'); });
      document.removeEventListener('keydown', onKey);
      box.classList.remove('is-open');
      document.body.classList.remove('soi-lock');
      setTimeout(() => box.remove(), 250);
      if (gallery.soiCarousel) gallery.soiCarousel.go(index);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowRight') go(index + 1);
      if (e.key === 'ArrowLeft') go(index - 1);
    };
    const toggleZoom = (fig, x, y) => {
      if (fig.classList.contains('is-video')) return;
      if (zoom) { resetZoom(); return; }
      const img = fig.querySelector('img');
      const r = img.getBoundingClientRect();
      zoom = { fig, img, scale: 2.4 };
      fig.classList.add('is-zoomed');
      const ox = ((x - r.left) / r.width) * 100;
      const oy = ((y - r.top) / r.height) * 100;
      img.style.transformOrigin = `${ox}% ${oy}%`;
      img.style.transform = `scale(${zoom.scale})`;
    };
    figs.forEach((fig) => {
      fig.addEventListener('click', (e) => toggleZoom(fig, e.clientX, e.clientY));
      fig.addEventListener('mousemove', (e) => {
        if (!zoom || zoom.fig !== fig) return;
        const r = fig.getBoundingClientRect();
        zoom.img.style.transformOrigin = `${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`;
      });
      fig.addEventListener('touchmove', (e) => {
        if (!zoom || zoom.fig !== fig || e.touches.length !== 1) return;
        e.preventDefault();
        const r = fig.getBoundingClientRect();
        const t = e.touches[0];
        zoom.img.style.transformOrigin = `${((t.clientX - r.left) / r.width) * 100}% ${((t.clientY - r.top) / r.height) * 100}%`;
      }, { passive: false });
    });
    onSwipe(box.querySelector('.soi-lightbox__viewport'), (d) => { if (!zoom) go(index + d); }, 45);
    box.querySelector('.soi-lightbox__close').addEventListener('click', close);
    box.querySelector('.soi-lightbox__arrow--prev').addEventListener('click', () => go(index - 1));
    box.querySelector('.soi-lightbox__arrow--next').addEventListener('click', () => go(index + 1));
    thumbs.forEach((t) => t.addEventListener('click', () => go(parseInt(t.dataset.i, 10))));
    document.addEventListener('keydown', onKey);
    track.style.transition = 'none';
    go(index);
    requestAnimationFrame(() => { track.style.transition = ''; box.classList.add('is-open'); box.querySelector('.soi-lightbox__close').focus(); });
  }
  function initLightbox(root) {
    $$('[data-soi-gallery]', root).forEach((gallery) => {
      let moved = false;
      let sx = 0;
      gallery.addEventListener('touchstart', (e) => { sx = e.touches[0].clientX; moved = false; }, { passive: true });
      gallery.addEventListener('touchmove', (e) => { if (Math.abs(e.touches[0].clientX - sx) > 10) moved = true; }, { passive: true });
      gallery.addEventListener('click', (e) => {
        if (moved || e.target.closest('button, a, .pdp-gallery-dots')) return;
        const slide = e.target.closest('[data-soi-zoom]');
        if (!slide) return;
        const all = $$('[data-soi-zoom]', gallery);
        openLightbox(gallery, all.indexOf(slide));
      });
    });
  }

  /* ---------- Collection filters (non-AJAX baseline; the app module upgrades to AJAX) ---------- */
  // Size / colour / category use Shopify's tag-filter paths, which carry no query string.
  // Fold the live query (sort, price, availability) into those links so nothing is lost on click.
  function keepQuery(root = document) {
    const params = new URLSearchParams(location.search);
    ['page', 'section_id'].forEach((k) => params.delete(k));
    const qs = params.toString();
    $$('[data-soi-keep-query]', root).forEach((a) => {
      const href = (a.getAttribute('href') || '').split('?')[0];
      a.setAttribute('href', qs ? href + '?' + qs : href);
    });
  }

  function initFilters(root) {
    const form = root.querySelector('[data-soi-filter-form]');
    if (!form) return;
    const drawerOpen = root.querySelector('[data-soi-filter-drawer-open]');
    let backdrop = root.querySelector('.soi-filter-backdrop');
    if (!backdrop) {
      backdrop = document.createElement('div');
      backdrop.className = 'soi-filter-backdrop';
      root.appendChild(backdrop);
    }
    const drawer = () => root.querySelector('[data-soi-filter-drawer]');
    const setOpen = (open) => {
      drawer()?.classList.toggle('is-open', open);
      backdrop.classList.toggle('is-open', open);
      drawerOpen?.setAttribute('aria-expanded', String(open));
      document.body.classList.toggle('soi-lock', open);
    };
    root.soiFilterDrawer = setOpen;
    drawerOpen?.addEventListener('click', () => setOpen(true));
    backdrop.addEventListener('click', () => setOpen(false));
    root.addEventListener('click', (e) => { if (e.target.closest('[data-soi-filter-drawer-close]')) setOpen(false); });

    const desktop = () => window.matchMedia('(min-width: 1100px)').matches;
    const ajax = () => !!(window.SOI && window.SOI.filters && window.SOI.filters.enabled);
    // Desktop dropdowns: only one open, close on outside click
    root.addEventListener('toggle', (e) => {
      const d = e.target;
      if (!(d instanceof HTMLDetailsElement) || !d.open || !desktop()) return;
      $$('[data-soi-fgroup]', root).forEach((o) => { if (o !== d) o.open = false; });
    }, true);
    document.addEventListener('click', (e) => {
      if (!desktop() || e.target.closest('[data-soi-fgroup]')) return;
      $$('[data-soi-fgroup][open]', root).forEach((o) => { o.open = false; });
    });

    const navigate = (url) => {
      if (emit('soi:filter:navigate', { url, root }, root)) window.location.href = url;
    };
    root.addEventListener('change', (e) => {
      if (ajax()) return; // app module handles it
      if (e.target.matches('[data-soi-category-select]')) { navigate(e.target.value); return; }
      if (e.target.matches('[data-soi-sort]') || desktop()) form.requestSubmit();
    });
    root.addEventListener('change', (e) => {
      if (ajax() && e.target.matches('[data-soi-category-select]')) navigate(e.target.value);
    });
    keepQuery(root);
  }

  /* ---------- Recommendations (section rendering API) ---------- */
  function initRecommendations(root) {
    const url = root.dataset.url;
    if (!url || root.dataset.loaded) return;
    const load = async () => {
      root.dataset.loaded = '1';
      try {
        const html = await (await fetch(url)).text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('[data-soi-recommendations]');
        if (fresh && fresh.innerHTML.trim()) {
          root.innerHTML = fresh.innerHTML;
          initScrollArrows(root);
          emit('soi:content:loaded', { root });
        }
      } catch (err) { /* recommendations are optional */ }
    };
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => { if (entries[0].isIntersecting) { io.disconnect(); load(); } }, { rootMargin: '400px' });
      io.observe(root);
    } else load();
  }
  function initScrollArrows(root) {
    const track = root.querySelector('[data-soi-scroll-track]');
    $$('[data-soi-scroll]', root).forEach((b) => b.addEventListener('click', () => {
      track?.scrollBy({ left: parseInt(b.dataset.soiScroll, 10) * track.clientWidth * 0.8, behavior: 'smooth' });
    }));
  }

  /* ---------- Init ---------- */
  function init(root = document) {
    $$('[data-soi-carousel]', root).forEach(initCarousel);
    $$('[data-soi-fader]', root).forEach(initFader);
    $$('[data-soi-maroon]', root).forEach(initMaroon);
    $$('[data-soi-stack]', root).forEach(initStack);
    $$('[data-soi-scroller]', root).forEach(initScroller);
    $$('[data-soi-video-toggle]', root).forEach(initVideoToggle);
    $$('[data-soi-stores]', root).forEach(initStores);
    $$('[data-soi-product]', root).forEach(initProduct);
    initLightbox(root);
    $$('[data-soi-filters-root]', root).forEach(initFilters);
    $$('[data-soi-recommendations]', root).forEach(initRecommendations);
    $$('[data-soi-recs-curated]', root).forEach(initScrollArrows);
    initTabs(root.querySelector ? (root.querySelector('[data-soi-drawer]') || document.createElement('div')) : document);
    $$('[data-soi-tabs]', root).forEach(initTabs);
    initAccordions(root);
    initBack(root);
    if (videoObserver) $$('video[data-soi-autoplay]', root).forEach((v) => videoObserver.observe(v));
  }

  // Script-delay apps (Tapita) and async injection can run this file after DOMContentLoaded has
  // already fired, in which case a plain listener never runs and no carousel/menu/drawer is wired.
  const boot = () => {
    initSticky();
    initAnnouncementOffset();
    initMega();
    initDrawer();
    initSearch();
    initEcomMega();
    init(document);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Filter regions are re-rendered by the app's AJAX module; re-apply the query carry-over.
  document.addEventListener('soi:content:loaded', () => keepQuery(document));

  // Theme editor support
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:block:select', (e) => {
    const carousel = e.target.closest('[data-soi-carousel]');
    if (carousel && carousel.soiCarousel) carousel.soiCarousel.go(carousel.soiCarousel.slides.indexOf(e.target));
  });

  window.SOITheme = { init, cart: Cart, emit };
})();
